import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { FC, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { isAccountOlderThanOneMonth } from '@utils/AccountAccess/accountAgeStatuses'
import { isKYCUpdateRequired } from '@utils/AccountAccess/accountKYCStatuses'
import { isEconomicProfileTestNeeded } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { TestAnswerOutcomeStatus } from '@model/ClientTestAnswerDto'
import { TickmillProductType, isTickmillProductTypeCFD } from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestTemplate } from '@shared/templates/TestTemplate'

export const ProductEconomicProfileSuccessPage: FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)

  const resolvedProductId = useResolvedAppTestProduct()
  const { refreshAccount } = useAccountWriteContext()
  const apiClient = useApiClient(ClientApiClient)
  const { getLandingPage } = useLandingPage()

  const isSQFProduct = resolvedProductId === TickmillProductType.SQF

  const { data: account, isLoading: isLoadingAccount } = useQuery({
    queryKey: queryKeys.productRegistration.account(locale),
    queryFn: () => apiClient.getAccountWithAppTestResults(locale),
  })

  const { data: appTestAnswers, isLoading: isLoadingAppTestAnswers } = useQuery({
    queryKey: ['productRegistration', 'appTestAnswers', locale, resolvedProductId],
    queryFn: () => apiClient.getAccountAppTestAnswers(locale, resolvedProductId),
    enabled: isSQFProduct,
  })

  const navigateWithRefresh = async (path?: string) => {
    await refreshAccount(locale)
    navigate(path ?? getLandingPage() ?? CommonPaths.TradersRoomWallets)
  }

  const isClientRegisteredMoreThanOneMonthAgo = useMemo(
    () => isAccountOlderThanOneMonth(account),
    [account]
  )

  const requiresKYCUpdate = isKYCUpdateRequired(account)
  const requiresEconomicProfile = isEconomicProfileTestNeeded(account, resolvedProductId)
  const isMarginalAccepted =
    isSQFProduct && appTestAnswers?.outcome?.outcome === TestAnswerOutcomeStatus.REFERRED
  const kycUpdateMessage = t(
    'In the next step, we ask you to fill-in your details to update your profile.'
  )
  let sqfSuccessMessage = t('SQF.result.passed')

  if (isMarginalAccepted) {
    sqfSuccessMessage = t('SQF.result.marginalAccepted')
  }

  const handleSuccessNavigation = () => {
    if (isSQFProduct) {
      return navigateWithRefresh(CommonPaths.TradersRoomWallets)
    }

    if (
      requiresKYCUpdate ||
      (isClientRegisteredMoreThanOneMonthAgo && isTickmillProductTypeCFD(resolvedProductId))
    ) {
      return navigateWithRefresh('/dashboard/update-kyc')
    }

    return navigateWithRefresh()
  }

  const successMessageDictionary: Record<TickmillProductType, string> = {
    [TickmillProductType.CFD]: requiresKYCUpdate
      ? t('Check your email inbox for the confirmation.')
      : t('You have now completed the registration for your CFD account!'),
    [TickmillProductType.ETD]: requiresKYCUpdate
      ? kycUpdateMessage
      : t('You have now completed the registration for your Futures account'),
    [TickmillProductType.SQF]: sqfSuccessMessage,
    [TickmillProductType.STOCKS]: kycUpdateMessage,
  }

  const renderAction = () => {
    if (isSQFProduct) {
      return (
        <Button.Primary fullWidth size='lg' onClick={handleSuccessNavigation}>
          {t('Close')}
        </Button.Primary>
      )
    }

    if (
      requiresKYCUpdate ||
      (isClientRegisteredMoreThanOneMonthAgo && isTickmillProductTypeCFD(resolvedProductId))
    ) {
      return (
        <Button.Primary fullWidth size='lg' onClick={handleSuccessNavigation}>
          {t('Update personal details')}
        </Button.Primary>
      )
    }

    if (requiresEconomicProfile) {
      return (
        <Button.Primary
          fullWidth
          size='lg'
          iconRight={ArrowIcon.Right}
          onClick={handleSuccessNavigation}
        >
          {t('Continue')}
        </Button.Primary>
      )
    }

    return (
      <Button.Primary
        fullWidth
        size='lg'
        iconRight={ArrowIcon.Right}
        onClick={handleSuccessNavigation}
      >
        {t('Continue')}
      </Button.Primary>
    )
  }

  return (
    <TestTemplate
      maxWidth={600}
      isLoading={isLoadingAccount || (isSQFProduct && isLoadingAppTestAnswers)}
      contentRef={contentRef}
      onClose={handleSuccessNavigation}
    >
      <ContentBlock
        variant='voucher'
        icon={<FeatureIcon size='lg' icon={CircleCheckIcon} bg='surface-success-high-em' />}
        title={t('Sign up.Economic Profile Complete')}
        description={
          isAccountActivatedStatus(account)
            ? successMessageDictionary[resolvedProductId]
            : [
                <>
                  {t("Sign up.We've emailed all of the relevant information to")}{' '}
                  <b>{account?.emails[0]?.address}</b>
                </>,
                t(
                  "Sign up.Let's get on to the next step – uploading your identification documents."
                ),
              ]
        }
      />
      <FullScreenFooter contentRef={contentRef}>{renderAction()}</FullScreenFooter>
    </TestTemplate>
  )
}
